import { useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

function parseValue(raw: unknown): { year: number; month: number } | null {
  if (typeof raw === "string" && /^\d{4}-\d{2}$/.test(raw)) {
    const [y, m] = raw.split("-").map(Number)
    return { year: y, month: m }
  }
  return null
}

export function MonthPickerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [open, setOpen] = useState(false)

  const now = new Date()
  const rawValue = element.bind ? resolveSymbol(element.bind) : undefined
  const parsed = parseValue(rawValue)
  const viewYear = parsed?.year ?? now.getFullYear()
  const [displayYear, setDisplayYear] = useState(viewYear)

  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : "Pick a month"

  const disabled = element.disabled === true

  const displayText = parsed
    ? `${MONTH_NAMES[parsed.month - 1]} ${parsed.year}`
    : placeholder

  const handleMonthSelect = (monthIndex: number) => {
    dispatchEvent(element.id, "change", {
      year: displayYear,
      month: monthIndex + 1,
    })
    setOpen(false)
  }

  return (
    <div
      className={cn("relative", element.className)}
      data-testid={`month-picker-${element.id}`}
    >
      <button
        type="button"
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          !parsed && "text-muted-foreground"
        )}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        aria-label={element.ariaLabel ?? placeholder}
      >
        <span>{displayText}</span>
        <svg
          className="ml-2 h-4 w-4 shrink-0 opacity-50"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-64 rounded-md border border-border bg-popover p-3 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-sm hover:bg-accent"
              onClick={() => setDisplayYear((y) => y - 1)}
              aria-label="Previous year"
            >
              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="text-sm font-bold" data-testid="year-display">
              {displayYear}
            </div>
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-sm hover:bg-accent"
              onClick={() => setDisplayYear((y) => y + 1)}
              aria-label="Next year"
            >
              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((label, index) => {
              const isSelected =
                parsed !== null &&
                parsed.month === index + 1 &&
                parsed.year === displayYear
              const isCurrent =
                now.getMonth() === index &&
                now.getFullYear() === displayYear

              return (
                <button
                  key={label}
                  type="button"
                  className={cn(
                    "h-9 rounded-md text-xs font-semibold",
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent hover:text-accent-foreground",
                    isCurrent &&
                      !isSelected &&
                      "border border-teal-200 text-teal-600 bg-teal-50/30"
                  )}
                  onClick={() => handleMonthSelect(index)}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
